import { FC, useCallback } from "react";
import { useTranslation } from "react-i18next";
import { Box, IconButton, Stack, styled, Typography } from "@mui/material";
import { IconEmojiEmotions, IconClose } from "@/assets/icons";
import { rounded, spacing } from "@/theme/variables";
import { divider, green, icon, lightGreen, text } from "@/theme/colors";
import { useStudySettings } from "@/infrastructure/adapters/user-preference/user-preference.hook";
import { useBoostMessage } from "@/infrastructure/adapters/study";
import { toast } from "@/utils/toast";
import { showError } from "@/utils/error";
import { EncouragementMessageSkeleton } from "@/features/dashboard/components/skeletons/EncouragementMessageSkeleton";

export const EncouragementMessage: FC = () => {
  const { t } = useTranslation();
  const { settings, updateSettings, isUpdating } = useStudySettings();
  const { message, isLoading } = useBoostMessage(settings?.isBoostVisible ?? false);

  const handleClose = useCallback(async () => {
    try {
      await updateSettings({ isBoostVisible: false });
      toast.success(t("common.saved"));
    } catch (error) {
      showError(error);
    }
  }, [updateSettings, t]);

  if (isLoading || isUpdating) {
    return <EncouragementMessageSkeleton />;
  }

  if (!settings?.isBoostVisible || !message || message.length === 0) {
    return null;
  }

  return (
    <CardWrapper>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Stack direction="row" alignItems="center">
          <IconEmojiEmotions color={green[500]} fontSize="24px" />
          <Typography variant="body14Bold" color={lightGreen[500]}>
            {t("dashboard.encouragement.title")}
          </Typography>
        </Stack>
        <IconButton onClick={handleClose} size="small" sx={{ p: 0 }} aria-label={t("common.close")}>
          <IconClose width={24} height={24} color={icon.default} />
        </IconButton>
      </Stack>

      <Box borderTop={`1px solid ${divider.high}`} />

      <Typography variant="body14Semi" color={text.primary} whiteSpace="pre-wrap">
        {message}
      </Typography>
    </CardWrapper>
  );
};

const CardWrapper = styled(Box)`
  border: 1px solid ${green[500]};
  border-radius: ${rounded.xs};
  background-color: ${green[50]};
  padding: ${spacing.smd} ${spacing.sm};
  display: flex;
  flex-direction: column;
  gap: ${spacing.xs};
`;
